import React, { createContext, useCallback, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import {
  ensureVolunteerProfile,
  fetchProfileByUserId,
  parseProfileRow,
  type ProfileRow,
} from '@/utils/ensureUserProfile';
import type { AdminOrg } from '@/hooks/useAdminOrganizations';

export type Certificate = {
  id: string;
  title: string;
  organization: string;
  date: string;
  image: string;
};

type UserType = {
  id: string;
  first_name?: string;
  last_name?: string;
  email: string;
  phone?: string;
  date_of_birth?: string;
  profile_image?: string;
  background_image?: string;
  role?: string;
  description?: string;
  age?: string | number;
  tshirtSize?: string;
  location?: string;
  skills?: string[];
  certificates?: Certificate[];
  onboarding_completed?: boolean;
  [key: string]: unknown;
};

type UserContextType = {
  user: UserType | null;
  setUser: (user: UserType | null) => void;
  updateUser: (updates: Partial<UserType>) => void;
  refreshOrganizationData: () => Promise<void>;
  /** True while loading or creating the profile row for the signed-in auth user. */
  profileLoading: boolean;
  /** Orgs this volunteer has been granted admin access to. Empty for org accounts. */
  adminOrgs: AdminOrg[];
  /** The org the volunteer is currently acting as admin of (null = volunteer view). */
  activeAdminOrgId: string | null;
  setActiveAdminOrgId: (id: string | null) => void;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

function profileToUserType(profile: ProfileRow): UserType {
  return {
    ...profile,
    id: profile.id,
    email: profile.email,
    subscription: profile.subscription ?? null,
  };
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserType | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [adminOrgs, setAdminOrgs] = useState<AdminOrg[]>([]);
  const [activeAdminOrgId, setActiveAdminOrgId] = useState<string | null>(null);
  const { user: authUser, session } = useAuth();
  const isLoadingRef = useRef(false);
  const loadedUserIdRef = useRef<string | null>(null);

  // Stable identity: consumers list it as a hook dependency (ProfilePage refetches when it
  // changes), so a new function per render would loop fetch -> updateUser -> refetch.
  const updateUser = useCallback((updates: Partial<UserType>) => {
    setUser((currentUser) => {
      if (!currentUser) return currentUser;

      if (updates.certificates) {
        return {
          ...currentUser,
          ...updates,
          certificates: updates.certificates,
        };
      }

      return {
        ...currentUser,
        ...updates,
        certificates: currentUser.certificates || [],
      };
    });
  }, []);

  const refreshOrganizationData = async () => {
    if (!authUser?.id || !user?.role || user.role !== 'organization') {
      return;
    }

    try {
      const { data: orgData, error: orgError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .eq('role', 'organization')
        .maybeSingle();

      if (orgData && !orgError) {
        const mergedUser = {
          ...user,
          profile_image: orgData.profile_image ?? user.profile_image,
          background_image: orgData.background_image ?? user.background_image ?? '',
          name: orgData.first_name ?? user.name ?? '',
          first_name: orgData.first_name ?? user.first_name ?? '',
          last_name: orgData.last_name ?? user.last_name ?? '',
          contact_email: orgData.email ?? user.contact_email ?? '',
          contact_phone: orgData.phone ?? user.contact_phone ?? '',
          location: orgData.location ?? user.location ?? '',
          description: orgData.description !== undefined ? orgData.description : user.description,
          subscription: orgData.subscription ?? user.subscription ?? null,
        };

        setUser(mergedUser);
      }
    } catch (error) {
      logger.error('Error refreshing organization data:', error);
    }
  };

  useEffect(() => {
    const currentAuthUserId = session?.user?.id ?? authUser?.id ?? null;

    if (!currentAuthUserId) {
      loadedUserIdRef.current = null;
      setUser(null);
      setProfileLoading(false);
      return;
    }

    if (loadedUserIdRef.current === currentAuthUserId) {
      return;
    }

    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setProfileLoading(true);
    let isCancelled = false;

    const loadProfileData = async () => {
      try {
        let profile: ProfileRow | null = null;

        const { data: existingProfile, error: fetchError } = await fetchProfileByUserId(
          currentAuthUserId,
        );

        if (fetchError) throw fetchError;

        if (existingProfile) {
          profile = parseProfileRow(existingProfile);
        }

        const activeAuthUser = session?.user ?? authUser;

        if (!profile && activeAuthUser) {
          profile = await ensureVolunteerProfile(activeAuthUser);
        }

        if (isCancelled) return;

        if (profile) {
          setUser(profileToUserType(profile));
          loadedUserIdRef.current = currentAuthUserId;

          if (profile.role === 'volunteer') {
            try {
              const { data: memData } = await supabase
                .from('organization_memberships')
                .select('organization_id')
                .eq('volunteer_id', currentAuthUserId)
                .eq('status', 'accepted')
                .eq('role', 'admin');

              const orgIds = ((memData ?? []) as { organization_id: string }[]).map(
                (r) => r.organization_id,
              );

              if (orgIds.length > 0) {
                const { data: profData } = await supabase
                  .from('profiles')
                  .select('id, first_name, profile_image')
                  .in('id', orgIds);

                const orgs: AdminOrg[] = ((profData ?? []) as {
                  id: string;
                  first_name: string | null;
                  profile_image: string | null;
                }[]).map((p) => ({
                  id: p.id,
                  name: p.first_name?.trim() || 'Organization',
                  profileImage: p.profile_image ?? null,
                }));
                setAdminOrgs(orgs);
              } else {
                setAdminOrgs([]);
              }
            } catch (adminErr) {
              logger.error('UserContext: failed to load admin orgs', adminErr);
              setAdminOrgs([]);
            }
          } else {
            setAdminOrgs([]);
            setActiveAdminOrgId(null);
          }
        } else {
          setUser(null);
          loadedUserIdRef.current = null;
        }
      } catch (error) {
        logger.error('UserContext: failed to load or create profile', error);
        if (!isCancelled) {
          setUser(null);
          loadedUserIdRef.current = null;
        }
      } finally {
        if (!isCancelled) {
          isLoadingRef.current = false;
          setProfileLoading(false);
        }
      }
    };

    void loadProfileData();

    return () => {
      isCancelled = true;
      isLoadingRef.current = false;
    };
    // Keyed on the user id and token only: the auth user object is rebuilt on every auth
    // event, and reloading the profile for each one would refetch it needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id, session?.access_token]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        updateUser,
        refreshOrganizationData,
        profileLoading,
        adminOrgs,
        activeAdminOrgId,
        setActiveAdminOrgId,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
